import React from 'react';
import { motion } from 'framer-motion';
import { CircleCheck, CreditCard, Printer, Sparkles } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];
const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } };

const BADGES = [
  { icon: CircleCheck, label: '5 Active POS Integrations', tone: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30', testId: 'hero-badge-pos' },
  { icon: CreditCard, label: 'Direct Stripe Automated Payouts', tone: 'text-primary bg-primary/10 border-primary/30', testId: 'hero-badge-stripe' },
  { icon: Printer, label: 'Wireless & Cloud Printer Ready', tone: 'text-slate-600 dark:text-slate-300 bg-slate-500/10 border-slate-500/30', testId: 'hero-badge-printer' },
];

export default function IntegrationsHero() {
  return (
    <section
      className="relative w-full overflow-hidden px-4 sm:px-6 pt-16 pb-14 md:pt-24 md:pb-20 bg-card border-b border-border"
      data-testid="integrations-hero"
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none hc-northlines opacity-40" />
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-4xl mx-auto text-center"
      >
        <motion.div variants={item} className="flex items-center justify-center gap-3 mb-5">
          <span aria-hidden className="h-4 w-1 bg-primary" />
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Integrations & Hardware Hub
          </p>
        </motion.div>

        <motion.h1
          variants={item}
          className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tighter text-foreground leading-[1.02]"
        >
          POS, Payments & Hardware Integrations for Canadian Restaurants
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto"
        >
          Connect your existing POS, automate online card payouts via Stripe, and fire orders
          directly to Epson or Star Micronics thermal printers—with zero manual re-entry.
        </motion.p>

        <motion.ul variants={item} className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {BADGES.map(({ icon: Icon, label, tone, testId }) => (
            <li
              key={label}
              data-testid={testId}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs md:text-sm font-semibold ${tone}`}
            >
              <Icon className="w-4 h-4" strokeWidth={2.5} />
              {label}
            </li>
          ))}
        </motion.ul>

        {/* AIEO direct-answer box */}
        <motion.div
          variants={item}
          data-testid="aieo-answer-box"
          className="mt-10 text-left bg-background border border-border border-l-4 border-l-primary p-5 md:p-6"
        >
          <div className="flex items-center gap-2 mb-2.5">
            <Sparkles className="w-4 h-4 text-primary" strokeWidth={2.5} />
            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-primary">
              Quick Answer
            </p>
          </div>
          <p className="text-sm md:text-base text-foreground/85 leading-relaxed">
            Hungry Canadian provides native 2-way integrations with Shopify POS, Lightspeed
            Restaurant, Square for Restaurants, Clover POS, and Deliverect. Online orders flow
            directly to kitchen displays or thermal hardware (Epson Bluetooth/Wi-Fi & Star
            Micronics CloudPRNT). Payment processing and automated merchant bank payouts are
            powered by Stripe Connect, supporting all Canadian debit and credit card transactions.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
